import { useState, useEffect, useRef } from 'react';
import { Stack, Progress, Container, Alert, Loader } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { FamilyProfile, OnboardingStep } from '../../types/familyProfile';
import { FamilySizeStep } from './FamilySizeStep';
import { DietaryRestrictionsStep } from './DietaryRestrictionsStep';
import { CookingPreferencesStep } from './CookingPreferencesStep';
import { CuisinePreferencesStep } from './CuisinePreferencesStep';
import { ProteinPreferencesStep } from './ProteinPreferencesStep';
import { FlavorProfileStep } from './FlavorProfileStep';
import { RecipeUploadStep } from './RecipeUploadStep';
import { GroceryConnectionStep } from './GroceryConnectionStep';
import { EmailVerificationStep } from './EmailVerificationStep';
import { ContactEmailStep } from './ContactEmailStep';
import { householdSegment } from '../../utils/householdSegment';
import {
  DEFAULT_ADULTS,
  DEFAULT_COOKING_SKILL,
  DEFAULT_KIDS,
  DEFAULT_PROTEIN_DIVERSITY,
  defaultIncludeLeftovers,
} from '../../utils/familyProfileDefaults';
import { trackProfileCompleteReddit } from '../../utils/redditPixel';
import { trackProfileCompleteMeta } from '../../utils/metaPixel';
import { TastePreview } from './TastePreview';

// How long to wait before the single retry of the initial profile load.
const PROFILE_RETRY_DELAY_MS = 750;

type OnboardingWizardProps = {
  onComplete: (profile: FamilyProfile) => void;
  onCancel?: () => void;
};

export const OnboardingWizard = ({ onComplete, onCancel }: OnboardingWizardProps) => {
  const { user } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [profile, setProfile] = useState<FamilyProfile>({
    adults: DEFAULT_ADULTS,
    kids: DEFAULT_KIDS,
    dietaryRestrictions: [],
    allergies: [],
    cuisinePreferences: [],
    avoidCuisines: [],
    cookingSkill: DEFAULT_COOKING_SKILL,
    dislikes: [],
    favoriteProteins: [],
    avoidProteins: [],
    proteinDiversity: DEFAULT_PROTEIN_DIVERSITY,
    flavorProfile: {},
    includeLeftovers: defaultIncludeLeftovers(DEFAULT_ADULTS, DEFAULT_KIDS),
    preferredCookingMethods: [],
    krogerConnected: false,
    krogerInfo: undefined,
    productPreferences: {
      brandPreference: 'no_preference',
      organicPreference: 'no_preference',
      pricePreference: 'no_preference',
    },
  });
  const [initialized, setInitialized] = useState(false);
  // Guards the one-shot profile load below. A ref, not the `initialized`
  // state, because state updates land too late to stop a second effect run
  // (StrictMode's double mount, or a new `user` identity after a token
  // refresh) from firing a duplicate fetch.
  const profileLoadStartedRef = useRef(false);
  // Once the user sets the leftovers switch themselves, family size stops
  // overriding it.
  const [leftoversEdited, setLeftoversEdited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  // Initialize profile from existing user data
  useEffect(() => {
    if (!user || profileLoadStartedRef.current) return;
    profileLoadStartedRef.current = true;

    const fetchProfile = async () => {
      const token = await user.getIdToken();
      return fetch(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
    };

    const initializeProfile = async () => {
      try {
        let response = await fetchProfile();

        // A seconds-old account can beat its own profile record into
        // existence, which comes back as a 401. That resolves on its own in a
        // few hundred ms, so retry once rather than stranding the user on an
        // error only a page reload clears.
        if (response.status === 401) {
          await new Promise((resolve) => setTimeout(resolve, PROFILE_RETRY_DELAY_MS));
          response = await fetchProfile();
        }

        if (response.ok) {
          const data = await response.json();
          const existingProfile = data.user?.familyProfile;
          setHasContactEmail(
            !!(data.user?.contactEmail || data.user?.pendingContactEmail)
          );
          
          if (existingProfile) {
            // Merge existing data with defaults
            const newProfile = {
              ...profile,
              ...existingProfile,
              // Ensure arrays are properly initialized
              dietaryRestrictions: existingProfile.dietaryRestrictions || [],
              allergies: existingProfile.allergies || [],
              cuisinePreferences: existingProfile.cuisinePreferences || [],
              avoidCuisines: existingProfile.avoidCuisines || [],
              dislikes: existingProfile.dislikes || [],
              favoriteProteins: existingProfile.favoriteProteins || [],
              avoidProteins: existingProfile.avoidProteins || [],
              proteinDiversity: existingProfile.proteinDiversity || DEFAULT_PROTEIN_DIVERSITY,
              flavorProfile: existingProfile.flavorProfile || {},
              includeLeftovers: existingProfile.includeLeftovers ?? defaultIncludeLeftovers(
                existingProfile.adults ?? profile.adults,
                existingProfile.kids ?? profile.kids
              ),
              preferredCookingMethods: existingProfile.preferredCookingMethods || [],
              productPreferences: existingProfile.productPreferences || {
                brandPreference: 'no_preference',
                organicPreference: 'no_preference',
                pricePreference: 'no_preference',
              },
            };
            setProfile(newProfile);
            // A saved choice is the user's own - don't re-derive it from family size
            if (existingProfile.includeLeftovers !== undefined) {
              setLeftoversEdited(true);
            }
          }

          // Set the current step from saved progress
          const savedStep = data.user?.onboardingStep;
          if (typeof savedStep === 'number' && savedStep >= 0) {
            setCurrentStepIndex(savedStep);
          }
        } else {
          console.log('OnboardingWizard: Profile fetch failed:', response.status);
          setError('Failed to load your profile. Please try again.');
        }
      } catch (error) {
        console.error('Error loading existing profile:', error);
        setError('Failed to load your profile. Please try again.');
      } finally {
        setInitialized(true);
      }
    };

    initializeProfile();
    // `profile` is deliberately not a dependency: this seeds it once, and
    // listing it would re-run the effect on every edit the user makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [emailVerifiedDismissed, setEmailVerifiedDismissed] = useState(false);
  const needsEmailVerification = !emailVerifiedDismissed
    && user?.providerData?.[0]?.providerId === 'password'
    && !user?.emailVerified;

  // Facebook can grant sign-in without an email address. Without one the user
  // is unreachable (no welcome or plan emails), so capture a contact address
  // up front. Mutually exclusive with email verification: password accounts
  // always have an email.
  const [contactEmailDismissed, setContactEmailDismissed] = useState(false);
  const [hasContactEmail, setHasContactEmail] = useState(false);
  const needsContactEmail = !contactEmailDismissed
    && !!user
    && !user.email
    && !hasContactEmail;

  // Define all onboarding steps
  const steps: OnboardingStep[] = [
    ...(needsEmailVerification ? [{
      id: 'email-verification',
      title: 'Verify your email',
      component: EmailVerificationStep,
    }] : []),
    ...(needsContactEmail ? [{
      id: 'contact-email',
      title: 'Add your email',
      component: ContactEmailStep,
    }] : []),
    {
      id: 'family-size',
      title: 'Family size',
      component: FamilySizeStep,
    },
    {
      id: 'dietary-restrictions',
      title: 'Dietary preferences',
      component: DietaryRestrictionsStep,
    },
    {
      id: 'cooking-preferences',
      title: 'Cooking preferences',
      component: CookingPreferencesStep,
    },
    {
      id: 'cuisine-preferences',
      title: 'Cuisine preferences',
      component: CuisinePreferencesStep,
    },
    {
      id: 'protein-preferences',
      title: 'Protein preferences',
      component: ProteinPreferencesStep,
    },
    {
      id: 'flavor-profile',
      title: 'Flavor profile',
      component: FlavorProfileStep,
    },
    {
      id: 'recipe-upload',
      title: 'Add your recipes',
      component: RecipeUploadStep,
    },
    {
      id: 'grocery-connection',
      title: 'Grocery connection',
      component: GroceryConnectionStep,
    },
  ];

  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const saveOnboardingStep = async (step: number) => {
    try {
      const token = await user!.getIdToken();
      await fetch(`${process.env.REACT_APP_API_URL}/api/user/onboarding-step`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ step }),
      });
    } catch (error) {
      console.error('OnboardingWizard: Failed to save step progress:', error);
      setError('Failed to save progress. Please try again.');
    }
  };

  const handleNext = async () => {
    if (isLastStep) {
      setIsCompleting(true);
      try {
        // Track onboarding completion
        if (user) {
          trackProfileCompleteReddit(user.uid);
          trackProfileCompleteMeta(user.uid);
        }
        await onComplete(profile);
      } catch (error) {
        console.error('Error completing onboarding:', error);
        setError('Failed to complete setup. Please try again.');
        setIsCompleting(false);
      }
      // Note: We don't set isCompleting to false on success because the component will unmount
    } else {
      // Email verification step: clear the flag so the step drops out of
      // the array. Index 0 naturally becomes family-size.
      if (currentStep.id === 'email-verification') {
        setEmailVerifiedDismissed(true);
        window.scrollTo(0, 0);
        return;
      }

      // Contact-email step persists through its own endpoint; dropping the
      // flag removes it from the array so the next step becomes index 0.
      if (currentStep.id === 'contact-email') {
        setContactEmailDismissed(true);
        window.scrollTo(0, 0);
        return;
      }

      // Save current step's data to backend before advancing
      const currentStepData = getStepValue(profile, currentStep.id);
      if (currentStepData) {
        saveProfileUpdates(currentStepData);
      }

      const nextStep = currentStepIndex + 1;
      setCurrentStepIndex(nextStep);
      // Save progress to backend
      saveOnboardingStep(nextStep);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    } else if (onCancel) {
      onCancel();
    }
  };

  const saveProfileUpdates = async (updates: Partial<FamilyProfile>) => {
    try {
      const token = await user!.getIdToken();
      
      // Save partial updates to backend (doesn't complete onboarding)
      await fetch(`${process.env.REACT_APP_API_URL}/api/user/partial-family-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ familyProfile: updates }),
      });
    } catch (error) {
      console.error('OnboardingWizard: Failed to save profile updates:', error);
      setError('Failed to save profile updates. Please try again.');
    }
  };

  // Map dietary restrictions and allergies to proteins that should be avoided
  // Only includes proteins from the commonProteins list in ProteinPreferencesStep:
  // Chicken, Beef, Pork, Fish, Salmon, Shrimp, Turkey, Tofu, Beans, Eggs, Cheese, Nuts
  const getAutoAvoidProteins = (dietaryRestrictions: string[], allergies: string[]): string[] => {
    const autoAvoid: string[] = [];

    // Vegetarian: avoid all meat and seafood
    if (dietaryRestrictions.includes('Vegetarian')) {
      autoAvoid.push('Chicken', 'Beef', 'Pork', 'Turkey', 'Fish', 'Salmon', 'Shrimp');
    }

    // Vegan: avoid all animal products
    if (dietaryRestrictions.includes('Vegan')) {
      autoAvoid.push('Chicken', 'Beef', 'Pork', 'Turkey', 'Fish', 'Salmon', 'Shrimp', 'Eggs', 'Cheese');
    }

    // Pescatarian: avoid meat but allow fish/seafood
    if (dietaryRestrictions.includes('Pescatarian')) {
      autoAvoid.push('Chicken', 'Beef', 'Pork', 'Turkey');
    }

    // Kosher: avoid pork and shellfish
    if (dietaryRestrictions.includes('Kosher')) {
      autoAvoid.push('Pork', 'Shrimp');
    }

    // Halal: avoid pork
    if (dietaryRestrictions.includes('Halal')) {
      autoAvoid.push('Pork');
    }

    // Map allergies to proteins
    if (allergies.includes('Fish')) {
      autoAvoid.push('Fish', 'Salmon');
    }

    if (allergies.includes('Shellfish')) {
      autoAvoid.push('Shrimp');
    }

    if (allergies.includes('Eggs')) {
      autoAvoid.push('Eggs');
    }

    if (allergies.includes('Milk')) {
      autoAvoid.push('Cheese');
    }

    if (allergies.includes('Soy')) {
      autoAvoid.push('Tofu');
    }

    if (allergies.includes('Tree Nuts') || allergies.includes('Peanuts')) {
      autoAvoid.push('Nuts');
    }

    // Return unique values
    return Array.from(new Set(autoAvoid));
  };

  const updateProfile = (updates: Partial<FamilyProfile>) => {
    const newProfile = { ...profile, ...updates };

    // Family size drives the leftovers default until the user picks for themselves
    if (!leftoversEdited && (updates.adults !== undefined || updates.kids !== undefined)) {
      newProfile.includeLeftovers = defaultIncludeLeftovers(newProfile.adults, newProfile.kids);
    }

    // If dietary restrictions or allergies changed, auto-update avoid proteins
    if (updates.dietaryRestrictions !== undefined || updates.allergies !== undefined) {
      const autoAvoid = getAutoAvoidProteins(
        updates.dietaryRestrictions ?? profile.dietaryRestrictions,
        updates.allergies ?? profile.allergies
      );

      // Compute what the OLD restrictions auto-avoided so we can remove stale entries
      const oldAutoAvoid = getAutoAvoidProteins(
        profile.dietaryRestrictions,
        profile.allergies
      );

      // Keep only manually-added avoids (those not produced by the old restrictions)
      const manualAvoids = (profile.avoidProteins || []).filter(
        p => !oldAutoAvoid.some(a => a.toLowerCase() === p.toLowerCase())
      );

      const combined = Array.from(new Set([...manualAvoids, ...autoAvoid]));

      // Remove from favorites if now in avoid list
      const currentFavorites = profile.favoriteProteins || [];
      const updatedFavorites = currentFavorites.filter(
        fav => !combined.some(avoid => avoid.toLowerCase() === fav.toLowerCase())
      );

      newProfile.avoidProteins = combined;
      newProfile.favoriteProteins = updatedFavorites;
    }

    setProfile(newProfile);
  };

  // Show loading while initializing profile
  if (!initialized) {
    return (
      <Container size="sm" style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader color="orange" size="lg" />
      </Container>
    );
  }

  const StepComponent = currentStep.component;

  // Steps where the taste preview makes sense (user is configuring taste preferences)
  const tastePreviewSteps = new Set([
    'dietary-restrictions',
    'cooking-preferences',
    'cuisine-preferences',
    'protein-preferences',
    'flavor-profile',
  ]);
  const showTastePreview = tastePreviewSteps.has(currentStep.id);

  return (
    <Container size="sm" py="xl">
      <Stack gap="xl">
        {/* Progress bar */}
        <Progress
          value={progress}
          size="sm"
          color="orange"
          radius="xl"
        />

        {/* Error Alert */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
            {error}
          </Alert>
        )}

        {/* Current step */}
        <StepComponent
          value={getStepValue(profile, currentStep.id)}
          onChange={(value) => updateStepValue(
            profile,
            currentStep.id,
            value,
            updateProfile,
            () => setLeftoversEdited(true)
          )}
          onNext={handleNext}
          onBack={handleBack}
          isLastStep={isLastStep}
          isFirstStep={isFirstStep}
          isCompleting={isCompleting}
          householdSegment={householdSegment(profile)}
        />

        {/* Taste preview - shows recipe matches as user fills in preferences */}
        <TastePreview profile={profile} visible={showTastePreview} />
      </Stack>
    </Container>
  );
};

// Helper functions to get/set values for each step
const getStepValue = (profile: FamilyProfile, stepId: string): any => {
  switch (stepId) {
    case 'family-size':
      return { adults: profile.adults, kids: profile.kids };
    case 'dietary-restrictions':
      return { dietaryRestrictions: profile.dietaryRestrictions, allergies: profile.allergies };
    case 'cooking-preferences':
      return {
        cookingSkill: profile.cookingSkill,
        includeLeftovers: profile.includeLeftovers,
        preferredCookingMethods: profile.preferredCookingMethods
      };
    case 'cuisine-preferences':
      return { cuisinePreferences: profile.cuisinePreferences, avoidCuisines: profile.avoidCuisines, dislikes: profile.dislikes, cuisineAdventure: profile.cuisineAdventure };
    case 'protein-preferences':
      return { favoriteProteins: profile.favoriteProteins, avoidProteins: profile.avoidProteins, proteinDiversity: profile.proteinDiversity };
    case 'flavor-profile':
      return { flavorProfile: profile.flavorProfile || {} };
    case 'recipe-upload':
      return null; // Recipe upload step handles its own state
    case 'grocery-connection':
      return {
        krogerConnected: profile.krogerConnected,
        krogerInfo: profile.krogerInfo,
        productPreferences: profile.productPreferences
      };
    default:
      return null;
  }
};

const updateStepValue = (
  profile: FamilyProfile,
  stepId: string,
  value: any,
  updateProfile: (updates: Partial<FamilyProfile>) => void,
  onLeftoversEdited: () => void
) => {
  switch (stepId) {
    case 'family-size':
      updateProfile({ adults: value.adults, kids: value.kids });
      break;
    case 'dietary-restrictions':
      updateProfile({ dietaryRestrictions: value.dietaryRestrictions, allergies: value.allergies });
      break;
    case 'cooking-preferences':
      if (value.includeLeftovers !== profile.includeLeftovers) {
        onLeftoversEdited();
      }
      updateProfile({
        cookingSkill: value.cookingSkill,
        includeLeftovers: value.includeLeftovers,
        preferredCookingMethods: value.preferredCookingMethods
      });
      break;
    case 'cuisine-preferences':
      updateProfile({ cuisinePreferences: value.cuisinePreferences, avoidCuisines: value.avoidCuisines, dislikes: value.dislikes, cuisineAdventure: value.cuisineAdventure });
      break;
    case 'protein-preferences':
      updateProfile({ favoriteProteins: value.favoriteProteins, avoidProteins: value.avoidProteins, proteinDiversity: value.proteinDiversity });
      break;
    case 'flavor-profile':
      updateProfile(value);
      break;
    case 'recipe-upload':
      // Recipe upload step handles its own state and API calls
      break;
    case 'grocery-connection':
      updateProfile({
        krogerConnected: value.krogerConnected,
        krogerInfo: value.krogerInfo,
        productPreferences: value.productPreferences
      });
      break;
  }
};
